import React, { useEffect, useRef, useState } from 'react'

import { useAppSelector } from 'redux/store'
import { isMeinBuero } from '@/utils/build-env'
import { isOnboardingOpen, setOnboardingOpen } from '@/modules/_helpers/openOnboardingModal'
import { isConfirmEmailOpen, setConfirmEmailOpen } from '@/modules/_helpers/openConfirmEmailModal'
import OnboardingModal from './onboarding-modal'
import ConfirmEmailModal, { CONFIRM_EMAIL_VARIANTS } from '@/modules/account/components/confirm-email/confirm-email-modal'
import { updateSubscriptionDetails } from '@/redux/ducks/subscription/details'
import { getOnboardingGate } from '@/redux/ducks/subscription/getOnboardingGate'
import { trackOrgamaxGaEvent } from '@/modules/base/helpers/trackOrgamaxGaEvent'

/**
 * Safety net: renders onboarding/confirm-email modals directly in the React tree.
 * The imperative call in setSubscriptionState uses UNSAFE_renderElement (portal),
 * which may not be initialized yet when called from a queueMicrotask during reload.
 * This component bypasses the portal mechanism entirely — MUI Dialog creates its
 * own portal, so the modals display correctly regardless of GlobalPortalProvider timing.
 * The isOnboardingOpen/isConfirmEmailOpen checks prevent duplicate modals if the
 * portal-based path already succeeded.
 */
const isMB = isMeinBuero()

const OnboardingGuard = () => {
  const { confirmation, loading } = useAppSelector(state => state.subscription.details) || {}
  const userId = useAppSelector(state => state.user.info.id)
  const wizardStep = useAppSelector(state => state.wizard.config.step)
  const subscriptions = useAppSelector(state => state.tenant?.info?.subscriptions)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [showConfirmEmail, setShowConfirmEmail] = useState(false)
  const hasChecked = useRef(false)

  useEffect(() => {
    if (hasChecked.current || loading || !userId || !confirmation || isMB) return

    if (wizardStep > 0 && !confirmation.onboardingFinished) return

    hasChecked.current = true

    // shared with updateSubscriptionDetails — the guard only mirrors the two account gates
    const gate = getOnboardingGate(confirmation)

    if (gate === 'confirmEmail' && !isConfirmEmailOpen()) {
      const subscriptionTypeTypesLabel = subscriptions?.map(s => s.typeOfSubscription).join(', ') || ''
      trackOrgamaxGaEvent('Register', 'Registrierung', `Erster Screen ${subscriptionTypeTypesLabel}`)
      setConfirmEmailOpen(true)
      setShowConfirmEmail(true)
    } else if (gate === 'onboarding' && !isOnboardingOpen()) {
      setOnboardingOpen(true)
      setShowOnboarding(true)
    }
  }, [loading, userId, confirmation, wizardStep, subscriptions])

  if (showOnboarding) {
    return (
      <OnboardingModal
        open
        onClose={() => {
          setOnboardingOpen(false)
          setShowOnboarding(false)
          updateSubscriptionDetails(true)
        }}
      />
    )
  }

  if (showConfirmEmail) {
    return (
      <ConfirmEmailModal
        showLogout
        variant={CONFIRM_EMAIL_VARIANTS.registration}
        open
        isModal
        title=""
        closeModal={() => { setConfirmEmailOpen(false); setShowConfirmEmail(false) }}
        onConfirm={() => {
          setConfirmEmailOpen(false)
          setShowConfirmEmail(false)
          updateSubscriptionDetails(true, true)
        }}
        gaData={{
          eventCategory: 'DOI',
          eventAction: 'afterRegister',
          eventLabel: `DOI durchgeführt ${subscriptions?.map(s => s.typeOfSubscription).join(', ') || ''}`,
        }}
      />
    )
  }

  return null
}

export default OnboardingGuard
